import { memo, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Fade, Typography } from '@mui/material';
import {
  Estate,
  EstateStatus,
  EstateTaskInstance,
  EstateTaskInstanceStatus,
  PortalPermissions,
  useEstate_GetEstateByIdQuery,
  useEstateShareMembership_GetEstateShareMembershipQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  User_GetCurrentUserDocument,
  useUser_CreateStripeCustomerMutation,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import EditEstateModal from 'pages/estates/components/edit-estate-modal';
import InviteUserModal from 'pages/portal-account/components/manage-portal-users/invite-user-modal';
import { isEstateEditable } from 'utils/estate';
import { canAddOrEditClientMatter, canAssignTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateStatusButton, { ButtonProps } from './components/estate-status-button';
import EstateTrusuiteSearchPaymentForm from './components/estate-trusuite-search-payment-form';
import { TaskGroups } from './components/task-groups';
import { useStyles } from './estate-checklist.styles';

interface Props {
  hideNav?: boolean;
  refetchEstateTaskInstanceCompletionPercentage?: Function;
}

export const EstateChecklist = memo(
  ({ hideNav = false, refetchEstateTaskInstanceCompletionPercentage }: Props) => {
    const history = useHistory();
    const classes = useStyles();
    const { portalId, estateId } = useParams<Record<string, string>>();

    const currentUser = useCurrentUser();
    const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
    const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

    const [editingEstateId, setEditingEstateId] = useState<string | undefined>();
    const [showEstatePaymentModal, setShowEstatePaymentModal] = useState(false);
    const [showInviteUserModal, setShowInviteUserModal] = useState(false);
    const [showHiddenTasks, setShowHiddenTasks] = useState<boolean>(false);

    const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
      variables: { id: estateId },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate by ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

    const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
      useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
        variables: {
          portalId,
        },
        onError: (e) => {
          showAlert({
            message: 'Error getting feature subscription type: ' + e.message,
            severity: NotificationSeverity.ERROR,
          });
        },
      });

    const { data: estateShareMembershipData, loading: estateShareMembershipLoading } =
      useEstateShareMembership_GetEstateShareMembershipQuery({
        variables: { input: { estateId, portalId } },
        onError: (e) => {
          showAlert({
            message: 'Error getting estate share membership: ' + e.message,
            severity: NotificationSeverity.ERROR,
          });
        },
      });

    // set up the stripe account for this user
    const [createStripeCustomer, { loading: creatingStripeCustomer }] =
      useUser_CreateStripeCustomerMutation({
        refetchQueries: [{ query: User_GetCurrentUserDocument }],
        onError: (e) => {
          showAlert({
            message: 'Error creating stripe customer: ' + e.message,
            severity: NotificationSeverity.ERROR,
          });
        },
      });

    const getCompletedGroupedTasksCount = useCallback(
      (groupedTasks: { [key: string]: EstateTaskInstance[] }, showHiddenTasks: boolean) => {
        const completedGroupedTasksCount: { [key: string]: number } = {};
        for (const group of Object.keys(groupedTasks)) {
          completedGroupedTasksCount[group] = groupedTasks[group].filter(
            (estateTaskInstance) =>
              estateTaskInstance.status === EstateTaskInstanceStatus.Completed &&
              estateTaskInstance.isHidden === showHiddenTasks,
          ).length;
        }
        return completedGroupedTasksCount;
      },
      [],
    );

    useEffect(() => {
      if (!creatingStripeCustomer && currentUser?.id && !currentUser?.stripeCustomerId) {
        createStripeCustomer({
          variables: {
            id: currentUser?.id || '',
          },
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      creatingStripeCustomer,
      createStripeCustomer,
      currentUser,
      getCompletedGroupedTasksCount,
      showHiddenTasks,
    ]);

    if (estateLoading || featureSubscriptionTypeLoading || estateShareMembershipLoading) {
      return <Spinner />;
    }

    if (hasPortalPermissionClient && !estateShareMembershipData?.data?.isActive) {
      // If shared client user, send to home page if they don't have access to this estate
      history.replace(`/home/${portalId}/home`);
      return <Spinner />;
    }

    const estate = estateData!.data;

    const onEditClientMatter = () => {
      if (!canAddOrEditClientMatter(featureSubscriptionTypeData!.data)) {
        showToast({
          message: 'Please upgrade your account to edit this client/matter.',
          severity: NotificationSeverity.WARNING,
        });
        return;
      }

      setEditingEstateId(estate.id);
    };

    const onOrderSearchButtonClick = () => {
      if (isEstateEditable(estate!.status)) {
        if (!canAssignTasks(featureSubscriptionTypeData!.data)) {
          showToast({
            message: 'Please upgrade your account to run this search.',
            severity: NotificationSeverity.WARNING,
          });
          return;
        }

        setEditingEstateId(undefined);
        setShowEstatePaymentModal(true);
      }
    };

    const estateSearchStatusButton = ({
      estateTaskInstance,
      locked,
      onAssignClick,
    }: ButtonProps) => (
      <EstateStatusButton
        estateTaskInstance={estateTaskInstance}
        status={estate.status!}
        loggedInUser={currentUser}
        locked={locked}
        launchEditModalFn={() => {
          if (!canAssignTasks(featureSubscriptionTypeData!.data)) {
            showToast({
              message: 'Please upgrade your account to run this search.',
              severity: NotificationSeverity.WARNING,
            });
            return;
          }

          if (estate?.status === EstateStatus.Payment) {
            setShowEstatePaymentModal(true);
            return false;
          }

          setEditingEstateId(estate.id);
        }}
        onAssignClick={onAssignClick}
      />
    );

    const content = (
      <Fade in={true}>
        <Box className={classes.dashboardContent}>
          {editingEstateId && (
            <EditEstateModal
              onExitClick={() => setEditingEstateId(undefined)}
              estateId={editingEstateId}
              onOrderSearchButtonClick={onOrderSearchButtonClick}
            />
          )}

          <CreatePaymentModal
            paymentForm={
              <EstateTrusuiteSearchPaymentForm
                setShowModal={setShowEstatePaymentModal}
                estate={estate}
              />
            }
            setShowModal={setShowEstatePaymentModal}
            showModal={showEstatePaymentModal}
          />
          {hasPortalPermissionAdmin && (
            <InviteUserModal
              showModal={showInviteUserModal}
              setShowModal={setShowInviteUserModal}
            />
          )}
          <Box className={classes.body}>
            <Box className={classes.primaryColumn}>
              {!hideNav && (
                <Typography className={classes.estateNameHeader} variant="h5">
                  {estate?.estateName || ''}
                </Typography>
              )}

              <TaskGroups
                estate={estate! as Estate}
                estateSearchStatusButton={estateSearchStatusButton}
                showHiddenTasks={showHiddenTasks}
                setShowHiddenTasks={setShowHiddenTasks}
                onEditClientMatterClick={onEditClientMatter}
                featureSubscriptionType={featureSubscriptionTypeData!.data}
                refetchEstateTaskInstanceCompletionPercentage={
                  refetchEstateTaskInstanceCompletionPercentage
                }
              />
            </Box>
          </Box>
        </Box>
      </Fade>
    );

    return hideNav ? content : <HomeLayout>{content}</HomeLayout>;
  },
);
